/**
 * 眼镜验光数据处理工具函数
 * 整合自AssignAstigmatism.vue、SuperMaster.vue和AdminOrders.vue
 */
import {ElMessage} from 'element-plus';

// 全局处理状态管理
const processingState = {
  validating: false,
  debounceTimers: new Map(),
};

// /**
//  * 防抖函数，避免函数在短时间内被多次调用
//  * @param {Function} fn - 需要执行的函数
//  * @param {number} delay - 延迟时间(毫秒)
//  * @param {string} key - 用于识别不同调用的唯一键
//  * @returns {Function} - 防抖处理后的函数
//  */
// function debounce(fn, delay, key) {
//   return function(...args) {
//     if (processingState.debounceTimers.has(key)) {
//       clearTimeout(processingState.debounceTimers.get(key));
//     }
//     const timer = setTimeout(() => {
//       fn.apply(this, args);
//       processingState.debounceTimers.delete(key);
//     }, delay);
//     processingState.debounceTimers.set(key, timer);
//   };
// }

/**
 * 验证输入是否为数字（可带正负号）
 * @param {string} value - 输入值
 * @param {string} fieldName - 字段名称（用于错误提示）
 * @param {boolean} formatOutput - 是否格式化输出（失焦时为true，输入时为false）
 * @returns {string} - 验证后的值
 */
export const validateNumericInput = (value, fieldName, formatOutput = false) => {
  // 允许单个"/"作为占位符，直接返回
  if (value === '/') {
    return value;
  }
  // 如果包含 "/" 但不止一个，或同时存在其他字符，则判为非法
  if (typeof value === 'string' && value.includes('/') ) {
    // 多个 / 或 "/" 与数字混用均视为非法输入
    ElMessage.warning(`${fieldName}只能输入数字或单个"/"占位符，已自动清空`);
    return '';
  }

  // 生成唯一键防止重复处理
  const uniqueKey = `validate_${fieldName}_${value}`;
  
  // 如果正在处理该字段，则跳过
  if (processingState.debounceTimers.has(uniqueKey)) {
    return value;
  }

  // // 调试信息
  // console.log(`validateNumericInput called: value=${value}, fieldName=${fieldName}, formatOutput=${formatOutput}`);

  // 允许空值
  if (value === '' || value === null || value === undefined) {
    return '';
  }


  // 处理形如 +.12 或 -.45 的情况，自动补全为 +0.12 或 -0.45
  if (value === '+.' || value === '-.') {
    value = value.replace('.', '0.');
  } else if (/^[+-]?\.\d*$/.test(value)) {
    value = value.replace(/^([+-])?\./g, '$10.');
  }

  // 允许用户正在输入的不完整数字
  // 允许: 单个符号(+/-)、数字加小数点、单个数字、空字符串等
  const partialNumericRegex = /^[+-]?\d*\.?\d*$/;

  if (partialNumericRegex.test(value)) {
    /*
     * 【新增逻辑】
     * 在不格式化(formatOutput === false)的情况下，也需要提前处理球镜/柱镜
     * 大于等于 100 的错误输入。
     */
    const isLensFieldEarly = fieldName.includes('球镜') || fieldName.includes('柱镜');
    const tempNum = parseFloat(value);
    if (isLensFieldEarly && !isNaN(tempNum) && Math.abs(tempNum) >= 100) {
      const converted = (tempNum / 100).toFixed(2);
      // 始终先提示一次
      ElMessage.warning(`检测到${fieldName}数值${value}绝对值大于等于100，已自动转换为${converted}`);
      if (!formatOutput) {
        return converted;
      }
      // 如果稍后还需要格式化，先把 value 替换为已转换的值，
      // 以便下面的格式化流程继续执行。
      value = converted;
    }

    // 如果不需要格式化（正在输入中），直接返回原始值
    if (!formatOutput) {
      return value;
    }

    // 以下逻辑仅在需要格式化时执行（如失焦时或回车时）

    // 如果是不完整的输入（只有符号、小数点等），转换为0
    if (value === '+' || value === '-' || value === '.' || value === '+.' || value === '-.') {
      return formatOutput ? '0.00' : value;
    }

    let numValue = parseFloat(value);

    // 判断是否为球镜或柱镜字段
    const isLensField = fieldName.includes('球镜') || fieldName.includes('柱镜');

    // 处理绝对值大于等于100的情况 - 只针对球镜和柱镜字段
    if (isLensField && Math.abs(numValue) >= 100) {
      // 新的转换逻辑：直接将数值缩小100倍
      const newValue = numValue / 100;

      ElMessage.warning(`检测到${fieldName}数值${value}绝对值大于等于100，已自动转换为${newValue.toFixed(2)}`);
      return newValue.toFixed(2);
    }

      // 对于需要格式化的数字，保留两位小数
      if (formatOutput) {
        return numValue.toFixed(2);
      } else {
        return value;
      }
    } else {
      // 如果不是数字，则清空输入
      ElMessage.warning(`${fieldName}只能输入数字，已自动清空`);
      return '';
    }
};

/**
 * 计算双眼瞳距
 * @param {Object} formData - 表单数据对象
 * @returns {void}
 */
export const calculateBinocularPupilDistance = (formData) => {
  // 如果任一瞳距值不存在或不是有效数字，则不计算
  if (!formData.odPupilDistance || !formData.osPupilDistance ||
      isNaN(parseFloat(formData.odPupilDistance)) || isNaN(parseFloat(formData.osPupilDistance))) {
    return;
  }

  const odDistance = parseFloat(formData.odPupilDistance) || 0;
  const osDistance = parseFloat(formData.osPupilDistance) || 0;

  if (odDistance && osDistance) {
    formData.binocularPupilDistance = (odDistance + osDistance).toFixed(2);
  }
};


/**
 * 判断字段是否需要格式化（保留小数点后两位）
 * @param {HTMLElement|string} input - 输入元素或字段名
 * @returns {boolean} - 如果需要格式化返回true，否则返回false
 */
function isFormatField(input) {
  if (!input) return false;

  // 获取输入框的ID、占位符或相关标签文本
  let fieldName = '';

  if (typeof input === 'string') {
    fieldName = input.toLowerCase();
  } else {
    // 检查输入类型
    if (!input.classList || !input.classList.contains('el-input__inner') ||
        input.type === 'date' || input.readOnly || input.disabled) {
      return false;
    }

    const id = input.id || '';
    const placeholder = input.placeholder || '';
    const label = input.previousElementSibling?.textContent || '';
    fieldName = (id + ' ' + placeholder + ' ' + label).toLowerCase();

    // 文本字段不进行格式化
    if (!isNumericField(input)) return false;
  }

  // 定义需要格式化的字段关键词列表（保留两位小数）
  const formatFieldKeywords = [
    '球镜', '柱镜', 'k1', 'k2', '眼轴', '瞳高', '瞳距', '双眼瞳距'
  ];

  // 检查是否包含需要格式化的字段关键词
  return formatFieldKeywords.some(keyword => fieldName.includes(keyword.toLowerCase()));
}

/**
 * 判断字段是否为数值输入字段（需要数值验证）
 * @param {HTMLElement|string} input - 输入元素或字段名
 * @returns {boolean} - 如果是数值字段返回true，否则返回false
 */
function isNumericField(input) {
  if (!input) return false;

  // 获取输入框的ID、占位符或相关标签文本
  let fieldName = '';
  if (typeof input === 'string') {
    fieldName = input.toLowerCase();
  } else {
    const id = input.id || '';
    const placeholder = input.placeholder || '';
    const label = input.previousElementSibling?.textContent || '';
    fieldName = (id + ' ' + placeholder + ' ' + label).toLowerCase();
  }

  // 定义所有需要数值验证的字段关键词列表
  const numericFieldKeywords = [
    '球镜', '柱镜', 'k1', 'k2', '眼轴', '瞳高', '瞳距', '双眼瞳距',
    '数量', '单价', '总价', '轴位', '脉络膜厚度'
  ];

  // 检查是否包含需要数值验证的字段关键词
  if (numericFieldKeywords.some(keyword => fieldName.includes(keyword.toLowerCase()))) {
    return true;
  }

  // 默认情况下，不是明确的文本字段且不包含数值关键词的字段认为不需要数值验证
  return false;
}



/**
 * 设置回车键导航功能
 * 为表单内所有输入元素添加回车键事件，按下回车后自动跳转到下一个输入框
 * @param {string} containerSelector - 表单容器的选择器
 * @returns {void}
 */
export const setupEnterKeyNavigation = (containerSelector = '.form-input') => {
  // 获取所有表单输入框，包括输入框、选择框和文本区域
  const inputs = document.querySelectorAll(`${containerSelector} input, ${containerSelector} textarea, ${containerSelector} .el-input__inner, .el-select-dropdown__item`);

  if (inputs.length === 0) return;

  // ===================== 新增公共格式化函数 =====================
  // 提取公共逻辑，避免 blur 与 keydown 中重复代码
  const formatNumericInputIfNeeded = (inputElement) => {
    const fieldName = inputElement.placeholder || inputElement.previousElementSibling?.textContent || '数值';
    if (
      inputElement.value &&
      inputElement.value !== '+' &&
      inputElement.value !== '-' &&
      inputElement.value !== '.' &&
      inputElement.value !== '+.' &&
      inputElement.value !== '-.'
    ) {
      const needFormat = isFormatField(inputElement);
      return validateNumericInput(inputElement.value, fieldName, needFormat);
    }
    return inputElement.value;
  };

  // 为输入框添加回车键事件监听
  inputs.forEach((input, index) => {
    // 如果已经设置了事件监听器，则跳过
    if (input.dataset.enterKeyHandlerAdded) return;
    input.dataset.enterKeyHandlerAdded = 'true';
    
    // 为输入框添加失焦事件，确保点击其他地方时也能格式化数值
    if (isNumericField(input)) {
      const blurHandler = () => {
        // 使用公共方法进行格式化，避免重复代码
        const formatted = formatNumericInputIfNeeded(input);
        if (formatted !== input.value) {
          input.value = formatted;
        }
      };
      
      safeAddEventListener(input, 'blur', blurHandler, 'enterKeyNavigationBlur');
    }

    const keydownHandler = (e) => {
      // 当按下回车键时
      if (e.key === 'Enter') {
        e.preventDefault(); // 阻止默认行为

        // 对于数值类输入框，尝试格式化，但只针对特定字段
        if (isNumericField(input)) {
          const formatted = formatNumericInputIfNeeded(input);
          if (formatted !== input.value) {
            input.value = formatted;
          }
        }

        // 如果不是最后一个输入框，则聚焦到下一个
        if (index < inputs.length - 1) {
          // 对于el-select组件，需要特殊处理
          if (inputs[index + 1].classList.contains('el-select-dropdown__item')) {
            // 如果下一个是下拉选项，可能需要先触发下拉框的点击
            const nextSelectInput = document.querySelector(`[aria-activedescendant="${inputs[index + 1].id}"]`);
            if (nextSelectInput) {
              nextSelectInput.click();
            } else {
              // 如果无法直接找到对应的select，则尝试跳到下下一个
              if (index + 2 < inputs.length) {
                inputs[index + 2].focus();
              }
            }
          } else {
            // 普通输入框直接聚焦
            inputs[index + 1].focus();
          }
        } else {
          // 如果是最后一个输入框，可以聚焦到提交按钮
          const submitButton = document.querySelector('.form-actions .el-button--primary');
          if (submitButton) {
            submitButton.focus();
          }
        }
      }
    };
    
    safeAddEventListener(input, 'keydown', keydownHandler, 'enterKeyNavigationKeydown');
  });
};

// 确保只对同一元素添加一次监听器
function safeAddEventListener(element, eventType, handler, handlerKey) {
  // 检查元素是否存在
  if (!element) return;
  
  // 创建或获取元素的事件处理器注册表
  if (!element._eventHandlers) {
    element._eventHandlers = {};
  }
  
  // 检查是否已经为该事件类型添加了处理器
  const key = `${eventType}_${handlerKey}`;
  if (element._eventHandlers[key]) {
    // 已添加过该事件处理器，移除旧的
    element.removeEventListener(eventType, element._eventHandlers[key]);
  }
  
  // 添加新的事件处理器并保存引用
  element.addEventListener(eventType, handler);
  element._eventHandlers[key] = handler;
}

// 抽离判断条件为复用函数
const is_valid_input = (value) => {
  return value &&
      value !== '+' &&
      value !== '-' &&
      value !== '.' &&
      value !== '+.' &&
      value !== '-.';
};

/**
 * 设置验光数据表单的输入验证和自动转换功能
 * @param {string} formPrefix - 表单元素ID前缀
 * @returns {void}
 */
export const setupOpticalFormInputs = (formPrefix) => {
  // 由于弹窗在关闭后会销毁 DOM，每次打开都需要重新挂载事件，因此不再对同一前缀做全局锁。利用元素级 data-* 标记避免重复绑定即可。

  // 数值输入验证函数
  const validateInput = (input, fieldName) => {
    if (!input) return; // 如果元素不存在，直接返回
    
    // 如果已经设置过事件监听器，则直接返回
    if (input.dataset.validationHandlerAdded) return;
    input.dataset.validationHandlerAdded = 'true';


    // input事件只进行基本验证，不格式化，保留原始输入
    const inputHandler = (e) => {
      // 只验证格式，不进行格式化，保留删除操作和正在编辑的值
      const value = e.target.value;
      if (!/^[+-]?\d*\.?\d*$/.test(value) && value !== '') {
        ElMessage.warning(`${fieldName}只能输入数字，已自动清空`);
        e.target.value = '';
      }
    };


     // 在失去焦点时进行完整验证和格式化
    const blurHandler = (e) => {
      // 只有当有值且不是正在编辑状态时才格式化
      if (is_valid_input(e.target.value)) {  // 使用抽离的函数

        // 判断是否需要格式化（保留两位小数）
        const needFormat = isFormatField(input);
        e.target.value = validateNumericInput(e.target.value, fieldName, needFormat);
      }
    };


// 添加回车键处理，与失焦效果相同
    const keydownHandler = (e) => {
      if (e.key === 'Enter') {
        // 只有当有值且不是正在编辑状态时才格式化
        if (is_valid_input(e.target.value)) {  // 使用抽离的函数

          const needFormat = isFormatField(input);
          e.target.value = validateNumericInput(e.target.value, fieldName, needFormat);
        }
      }
    };

    // 事件监听保持不变
    safeAddEventListener(input, 'input', inputHandler, 'validateInput');
    safeAddEventListener(input, 'blur', blurHandler, 'validateBlur');
    safeAddEventListener(input, 'keydown', keydownHandler, 'validateKeydown');
  };



  // 计算双眼瞳距的函数
  const calculateBinocularDistance = (odInput, osInput, binocularInput) => {
    if (!odInput || !osInput || !binocularInput) return;
    
    // 防止重复设置
    if (odInput.dataset.distanceHandlerAdded && osInput.dataset.distanceHandlerAdded) return;
    odInput.dataset.distanceHandlerAdded = 'true';
    osInput.dataset.distanceHandlerAdded = 'true';

    // 监听右眼和左眼瞳距的失焦事件，而不是输入事件
    const updateBinocular = () => {
      const odValue = parseFloat(odInput.value) || 0;
      const osValue = parseFloat(osInput.value) || 0;

      if (odValue && osValue) {
        binocularInput.value = (odValue + osValue).toFixed(1);
      }
    };

    // 使用安全的事件监听器添加方式
    safeAddEventListener(odInput, 'blur', updateBinocular, 'updateBinocularDistance');
    safeAddEventListener(osInput, 'blur', updateBinocular, 'updateBinocularDistance');
  };

  // 设置常规验光数据转换
  const setupRegularOpticalData = () => {
    // 常规验光 - 右眼
    const regularOdSphereInput = document.getElementById(`${formPrefix}-regularOdSphere`);
    const regularOdCylinderInput = document.getElementById(`${formPrefix}-regularOdCylinder`);
    const regularOdAxisInput = document.getElementById(`${formPrefix}-regularOdAxis`);

    // 常规验光 - 左眼
    const regularOsSphereInput = document.getElementById(`${formPrefix}-regularOsSphere`);
    const regularOsCylinderInput = document.getElementById(`${formPrefix}-regularOsCylinder`);
    const regularOsAxisInput = document.getElementById(`${formPrefix}-regularOsAxis`);


    // 添加右眼常规验光事件监听
    if (regularOdSphereInput) {
      validateInput(regularOdSphereInput, '球镜');
    }
    if (regularOdCylinderInput) {
      validateInput(regularOdCylinderInput, '柱镜');
    }
    if (regularOdAxisInput) {
      validateInput(regularOdAxisInput, '轴位');
    }

    // 添加左眼常规验光事件监听
    if (regularOsSphereInput) {
      validateInput(regularOsSphereInput, '球镜');
    }
    if (regularOsCylinderInput) {
      validateInput(regularOsCylinderInput, '柱镜');
    }
    if (regularOsAxisInput) {
      validateInput(regularOsAxisInput, '轴位');
    }
  };

  // 设置赋像散/超主点验光数据转换
  const setupSuperOpticalData = () => {
    // 赋像散/超主点验光 - 右眼
    const superOdSphereInput = document.getElementById(`${formPrefix}-superOdSphere`);
    const superOdCylinderInput = document.getElementById(`${formPrefix}-superOdCylinder`);
    const superOdAxisInput = document.getElementById(`${formPrefix}-superOdAxis`);
    const odQuantityInput = document.getElementById(`${formPrefix}-odQuantity`);

    // 赋像散/超主点验光 - 左眼
    const superOsSphereInput = document.getElementById(`${formPrefix}-superOsSphere`);
    const superOsCylinderInput = document.getElementById(`${formPrefix}-superOsCylinder`);
    const superOsAxisInput = document.getElementById(`${formPrefix}-superOsAxis`);
    const osQuantityInput = document.getElementById(`${formPrefix}-osQuantity`);


    // 添加右眼赋像散/超主点验光事件监听
    if (superOdSphereInput) {
      validateInput(superOdSphereInput, '球镜');
    }
    if (superOdCylinderInput) {
      validateInput(superOdCylinderInput, '柱镜');
    }
    if (superOdAxisInput) {
      validateInput(superOdAxisInput, '轴位');
    }
    if (odQuantityInput) {
      validateInput(odQuantityInput, '数量');
    }

    // 添加左眼赋像散/超主点验光事件监听
    if (superOsSphereInput) {
      validateInput(superOsSphereInput, '球镜');
    }
    if (superOsCylinderInput) {
      validateInput(superOsCylinderInput, '柱镜');
    }
    if (superOsAxisInput) {
      validateInput(superOsAxisInput, '轴位');
    }
    if (osQuantityInput) {
      validateInput(osQuantityInput, '数量');
    }
  };

  // 设置角膜曲率数据验证
  const setupKeratometryData = () => {
    // 角膜曲率 - 右眼
    const odK1Input = document.getElementById(`${formPrefix}-odK1`);
    const odK2Input = document.getElementById(`${formPrefix}-odK2`);
    const odAxialLengthInput = document.getElementById(`${formPrefix}-odAxialLength`);
    const odChoroidalThicknessInput = document.getElementById(`${formPrefix}-odChoroidalThickness`);

    // 角膜曲率 - 左眼
    const osK1Input = document.getElementById(`${formPrefix}-osK1`);
    const osK2Input = document.getElementById(`${formPrefix}-osK2`);
    const osAxialLengthInput = document.getElementById(`${formPrefix}-osAxialLength`);
    const osChoroidalThicknessInput = document.getElementById(`${formPrefix}-osChoroidalThickness`);

    // 添加数值验证
    if (odK1Input) validateInput(odK1Input, 'K1');
    if (odK2Input) validateInput(odK2Input, 'K2');
    if (odAxialLengthInput) validateInput(odAxialLengthInput, '眼轴');
    if (odChoroidalThicknessInput) validateInput(odChoroidalThicknessInput, '脉络膜厚度');

    if (osK1Input) validateInput(osK1Input, 'K1');
    if (osK2Input) validateInput(osK2Input, 'K2');
    if (osAxialLengthInput) validateInput(osAxialLengthInput, '眼轴');
    if (osChoroidalThicknessInput) validateInput(osChoroidalThicknessInput, '脉络膜厚度');


  };

  // 设置眼镜参数数据验证和计算
  const setupGlassesData = () => {
    // 眼镜参数
    const odPupilHeightInput = document.getElementById(`${formPrefix}-odPupilHeight`);
    const odPupilDistanceInput = document.getElementById(`${formPrefix}-odPupilDistance`);
    const osPupilHeightInput = document.getElementById(`${formPrefix}-osPupilHeight`);
    const osPupilDistanceInput = document.getElementById(`${formPrefix}-osPupilDistance`);
    const binocularPupilDistanceInput = document.getElementById(`${formPrefix}-binocularPupilDistance`);

    // 添加数值验证
    if (odPupilHeightInput) validateInput(odPupilHeightInput, '瞳高');
    if (osPupilHeightInput) validateInput(osPupilHeightInput, '瞳高');

    // 设置瞳距验证和计算
    if (odPupilDistanceInput && osPupilDistanceInput && binocularPupilDistanceInput) {
      validateInput(odPupilDistanceInput, '瞳距');
      validateInput(osPupilDistanceInput, '瞳距');
      validateInput(binocularPupilDistanceInput, '双眼瞳距');

      // 添加计算逻辑，使用新的计算函数
      calculateBinocularDistance(odPupilDistanceInput, osPupilDistanceInput, binocularPupilDistanceInput);
    }
  };

  // 执行各个模块的设置
  setupRegularOpticalData();
  setupSuperOpticalData();
  setupKeratometryData();
  setupGlassesData();

  // // 添加其他数值字段的验证
  // const ageInput = document.getElementById(`${formPrefix}-age`);
};


/**
 * 设置监听DOM变化，确保在动态元素加载后也能设置回车导航
 * @returns {MutationObserver} - DOM变化观察器
 */
export const setupEnterKeyNavigationWithObserver = () => {
  // 防止重复设置
  if (window._enterKeyNavigationSetupDone) return window._enterKeyObserver;
  window._enterKeyNavigationSetupDone = true;

  // 首先尝试直接设置
  setupEnterKeyNavigation();


  // 监听DOM变化
  const observer = new MutationObserver((mutations) => {
    let setupNeeded = false;
    
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        // 检查是否添加了新的输入框元素
        mutation.addedNodes.forEach(node => {
          if (node.querySelectorAll) {
            const inputs = node.querySelectorAll('input, textarea, .el-input__inner, .el-select-dropdown__item');
            if (inputs.length > 0) {
              setupNeeded = true;
            }
          }
        });
      }
    });
    
    // 只在真正需要时设置回车键导航
    if (setupNeeded) {
      setupEnterKeyNavigation();

    }
  });

  // 开始观察document.body的变化
  observer.observe(document.body, { childList: true, subtree: true });

  // 额外处理el-select组件，因为它们可能在用户交互后才完全渲染
  const handleSelectComponents = () => {
    // 防止重复设置
    if (window._selectComponentsHandled) return;
    window._selectComponentsHandled = true;
    
    const selects = document.querySelectorAll('.el-select');
    selects.forEach(select => {
      select.addEventListener('click', () => {
        // 在下拉框打开后，重新设置回车键导航
        setTimeout(setupEnterKeyNavigation, 100);
      });
    });
  };

  // 初始化选择组件的处理
  setTimeout(handleSelectComponents, 500);

  // 保存观察器引用以便可能的后续清理
  window._enterKeyObserver = observer;
  return observer;
};

/**
 * 获取当前用户的采购单位并设置到表单
 * @param {Object} formData - 表单数据对象，需要包含purchasingUnits属性
 * @param {Object} axios - axios实例，用于发送网络请求
 * @returns {Promise<void>}
 */
export const getUserPurchasingUnits = (formData, axios) => {
  try {
    // console.log('正在获取用户采购单位信息...');
    // 先尝试直接从localStorage获取userInfo
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    // console.log('获取到的用户信息:', userInfo);

    if (userInfo && userInfo.purchasingUnits) {
      // console.log('找到采购单位:', userInfo.purchasingUnits);
      formData.purchasingUnits = userInfo.purchasingUnits;
      return Promise.resolve(userInfo.purchasingUnits);
    } else {
      // 如果userInfo中没有purchasingUnits，尝试从其他可能的来源获取
      const userId = localStorage.getItem('userId');
      if (userId) {
        // console.log('尝试使用userId获取用户详细信息...');
        // 从后端获取用户信息
        return axios.get(`/api/v1/user/${userId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }).then(response => {
          if (response.data.code === '200' && response.data.data) {
            const userData = response.data.data;
            // console.log('从API获取的用户数据:', userData);
            if (userData.purchasingUnits) {
              // console.log('从API获取到采购单位:', userData.purchasingUnits);
              formData.purchasingUnits = userData.purchasingUnits;
              // 更新localStorage中的userInfo
              localStorage.setItem('userInfo', JSON.stringify(userData));
              return userData.purchasingUnits;
            }
          }
          return null;
        }).catch(error => {
          console.error('获取用户信息失败:', error);
          return null;
        });
      }
    }
    return Promise.resolve(null);
  } catch (error) {
    // console.error('获取用户采购单位失败:', error);
    return Promise.resolve(null);
  }
};


/**
 * 设置观察DOM变化，当表单被添加到DOM时设置验光数据处理功能
 * @returns {MutationObserver} - DOM变化观察器
 */
export const setupOpticalDataObserver = () => {
  // 防止重复设置
  if (window._opticalDataObserverSetupDone) return window._opticalDataObserver;
  window._opticalDataObserverSetupDone = true;

  const observer = new MutationObserver((mutations) => {
    let setupNeeded = false;
    
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        // 检查是否添加了新的表单元素
        for (let i = 0; i < mutation.addedNodes.length; i++) {
          const node = mutation.addedNodes[i];
          if (node.nodeType === 1 && node.id && 
             (node.id === 'edit-patientName' || 
              node.id === 'edit-fuxiangsan-patientName')) {
            setupNeeded = true;
            break;
          }
          
          // 检查子节点
          if (node.querySelectorAll) {
            const formInputs = node.querySelectorAll('#edit-patientName, #edit-fuxiangsan-patientName');
            if (formInputs.length > 0) {
              setupNeeded = true;
              break;
            }
          }
        }
      }
    });
    
    // 只在需要时设置表单处理
    if (setupNeeded) {
      // 检查是否有编辑表单，并设置相应的表单处理
      if (document.getElementById('edit-patientName')) {
        setupOpticalFormInputs('edit');
      }
      if (document.getElementById('edit-fuxiangsan-patientName')) {
        setupOpticalFormInputs('edit-fuxiangsan');
      }
    }
  });

  // 开始观察document.body的变化
  observer.observe(document.body, { childList: true, subtree: true });

  // 保存观察器引用以便可能的后续清理
  window._opticalDataObserver = observer;
  return observer;
};

// === 默认值填充工具 ===
/**
 * 将柱镜、轴位等必填数值字段的空字符串自动填充为 "0.00"
 * 目前适用于 SuperMaster、AssignAstigmatism 等订单页面
 * @param {Object} formData - 响应式表单对象
 * @returns {void}
 */
export const fillDefaultZeroFields = (formData) => {
  if (!formData) return;

  const defaultZeroFields = [
    'regularOdCylinder', 'regularOsCylinder', 'superOdCylinder', 'superOsCylinder',
    'regularOdAxis',     'regularOsAxis',     'superOdAxis',     'superOsAxis'
  ];

  let changed = false;
  defaultZeroFields.forEach(key => {
    if (formData[key] === '' || formData[key] === null || formData[key] === undefined) {
      formData[key] = '0.00';
      changed = true;
    }
  });

  if (changed) {
    ElMessage.info('部分柱镜/轴位为空，已自动填充为 0.00');
  }
};
// === 默认值填充工具 End ===

// === 控制字符清理工具 ===
/**
 * 清理字符串中的控制字符，统一换行符
 * 移除可能导致JSON解析错误的控制字符，特别是CR(code 13)
 * @param {string} str - 需要清理的字符串
 * @returns {string} - 清理后的字符串
 */
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return str;
  
  return str
    .replace(/\r\n/g, '\n')  // Windows换行符(CRLF) → Unix换行符(LF)
    .replace(/\r/g, '\n')     // 旧Mac换行符(CR) → Unix换行符(LF)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ''); // 移除其他控制字符（保留\t和\n）
};

/**
 * 清理表单对象中所有字符串字段的控制字符
 * 遍历对象的所有属性，对字符串类型的值进行清理
 * @param {Object} formData - 表单数据对象
 * @returns {Object} - 清理后的表单数据对象
 */
export const sanitizeFormData = (formData) => {
  if (!formData || typeof formData !== 'object') return formData;
  
  const cleaned = {};
  Object.keys(formData).forEach(key => {
    const value = formData[key];
    // 只处理字符串类型的值
    cleaned[key] = typeof value === 'string' ? sanitizeString(value) : value;
  });
  
  return cleaned;
};
// === 控制字符清理工具 End ===

// === 对数视力 → 小数视力转换（方案B）===
/**
 * 标准对数视力法 → 小数记录视力法 对照表（参考视力转换表）
 */
const LOG_VISION_TO_DECIMAL = {
  '4.0': '0.1', '4.1': '0.12', '4.2': '0.15', '4.3': '0.2', '4.4': '0.25',
  '4.5': '0.3', '4.6': '0.4', '4.7': '0.5', '4.8': '0.6', '4.9': '0.8',
  '5.0': '1.0', '5.1': '1.2', '5.2': '1.5'
};

/**
 * 单条视力值：若为对数视力则转为小数视力，带后缀时只转换符号前数字并保留后缀
 * @param {string} value - 原始输入，如 "4.8-"、"5.0+3"、"1.0"
 * @returns {string} - 转换后或原样返回
 */
export const convertLogVisionToDecimal = (value) => {
  if (value === null || value === undefined) return value;
  const str = String(value).trim();
  if (str === '') return value;

  const match = str.match(/^\s*(\d(?:\.\d+)?)\s*(.*)$/);
  if (!match) return value;
  const numStr = match[1];
  const suffix = match[2] || '';
  const num = parseFloat(numStr);
  if (Number.isNaN(num)) return value;

  const key = Number.isInteger(num) ? `${num}.0` : numStr;
  const decimal = LOG_VISION_TO_DECIMAL[key];
  if (decimal === undefined) return value;

  return decimal + suffix;
};

/** 订单表单中需要做对数→小数转换的视力字段名（双眼视力 + 单眼视力） */
export const VISION_FIELD_NAMES = [
  'regularOdVision', 'regularOsVision', 'regularBinocularVision',
  'superOdVision', 'superOsVision', 'superBinocularVision'
];

/**
 * 对表单对象中的视力字段做对数→小数转换（原地修改）
 * @param {Object} formData - 表单数据对象
 * @param {string[]} [fieldNames] - 视力字段名列表，默认使用 VISION_FIELD_NAMES
 * @returns {boolean} - 是否有字段被转换
 */
export const convertVisionFieldsInForm = (formData, fieldNames = VISION_FIELD_NAMES) => {
  if (!formData || typeof formData !== 'object') return false;
  let changed = false;
  fieldNames.forEach(key => {
    const val = formData[key];
    if (val === null || val === undefined || typeof val !== 'string') return;
    const converted = convertLogVisionToDecimal(val);
    if (converted !== val) {
      formData[key] = converted;
      changed = true;
    }
  });
  if (changed) {
    ElMessage.info('部分视力已从对数转换为小数记录');
  }
  return changed;
};
// === 对数视力 → 小数视力转换 End ===

// === 型号匹配工具已移至后端处理 ===
